import React, {useState, useEffect, useRef} from 'react';
import './Home.css';
const API_URL = import.meta.env.VITE_API_URL;
import axios from 'axios';

const Home = ({onNavigate, isAuthenticated}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [installationImages, setInstallationImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [partners, setPartners] = useState([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(true);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef(null);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoVolume, setVideoVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(true);
  const videoContainerRef = useRef(null);

  // Hook personnalisé pour l'animation des compteurs
  const useCountUp = (end, duration = 2000, shouldStart = false) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!shouldStart) return;

      let startTime;
      let animationFrame;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);

        // Fonction d'easing pour un effet plus naturel
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);

        setCount(Math.floor(easeOutQuart * end));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);

      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }, [end, duration, shouldStart]);

    return count;
  };

  // Intersection Observer pour déclencher l'animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
          }
        });
      },
      {threshold: 0.3}
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [hasAnimated]);

  // Compteurs animés
  const yearsCount = useCountUp(10, 2000, hasAnimated);
  const officesCount = useCountUp(4, 2000, hasAnimated);
  const solutionsCount = useCountUp(100, 2000, hasAnimated);

  // Récupérer les images hero depuis l'API
  useEffect(() => {
    const fetchHeroImages = async () => {
      try {
        const response = await axios.get(`${API_URL}/hero-images/active`);
        const images = response.data.data;

        if (images && images.length > 0) {
          // Trier par ordre
          const sortedImages = images.sort((a, b) => a.order - b.order);
          // Convertir les URLs pour utiliser l'API (retirer /api pour accéder aux uploads)
          const baseUrl = API_URL.replace('/api', '');
          const imageUrls = sortedImages.map(img => `${baseUrl}${img.imageUrl}`);
          setInstallationImages(imageUrls);
        } else {
          // Images par défaut si aucune image n'est disponible
          setInstallationImages([
            '/home/photo1.jpeg',
            '/home/photo2.jpeg',
            '/home/photo3.jpeg',
            '/home/photo4.jpeg'
          ]);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des images hero:', error);
        // En cas d'erreur, utiliser les images par défaut
        setInstallationImages([
          '/home/photo1.jpeg',
          '/home/photo2.jpeg',
          '/home/photo3.jpeg',
          '/home/photo4.jpeg'
        ]);
      } finally {
        setLoadingImages(false);
      }
    };

    fetchHeroImages();
  }, []);

  useEffect(() => {
    // Changer l'image toutes les 4 secondes pour le diaporama
    if (installationImages.length > 0) {
      const imageInterval = setInterval(() => {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === installationImages.length - 1 ? 0 : prevIndex + 1
        );
      }, 4000);

      return () => clearInterval(imageInterval);
    }
  }, [installationImages]);


  // 🔹 GÉOLOCALISATION (Optimisée : une seule fois par session visiteur)
  useEffect(() => {
    const hasTracked = sessionStorage.getItem('sastra_location_tracked');
    if (hasTracked) return;

    const sendLocation = async (latitude, longitude) => {
      try {
        await axios.post(`${API_URL}/dashboard/track-location`, {
          latitude,
          longitude,
        });
        sessionStorage.setItem('sastra_location_tracked', 'true');
      } catch (err) {
        // Erreur silencieuse
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          sendLocation(lat, lon);
        },
        (err) => {
          console.warn("Géolocalisation impossible :", err);
        }
      );
    } else {
      console.warn("La géolocalisation n'est pas supportée par ce navigateur.");
    }
  }, [API_URL]);

  // Récupérer les partenaires depuis l'API
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${API_URL}/partners?publicOnly=true`);
        setPartners(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Erreur lors du chargement des partenaires:', error);
        setPartners([]);
      } finally {
        setIsLoadingPartners(false);
      }
    };

    fetchPartners();
  }, [API_URL]);

  // ✨ Animations anime.js au chargement de la page
  useEffect(() => {
    if (typeof window.anime !== 'function') return; // Attendre que anime.js soit chargé depuis le CDN

    // Animation des cartes de service
    window.anime({
      targets: '.service-card',
      opacity: [0, 1],
      translateY: [60, 0],
      scale: [0.9, 1],
      duration: 1000,
      easing: 'easeOutElastic(1, .6)',
      delay: window.anime.stagger(100)
    });

    // Animation des features avec effet de slide
    window.anime({
      targets: '.feature',
      opacity: [0, 1],
      translateX: [-50, 0],
      duration: 800,
      easing: 'easeOutQuad',
      delay: window.anime.stagger(150, {start: 500})
    });

    // Animation flottante continue pour les icônes de service
    window.anime({
      targets: '.service-icon',
      translateY: [-10, 10],
      duration: 2000,
      easing: 'easeInOutSine',
      direction: 'alternate',
      loop: true,
      delay: window.anime.stagger(200)
    });

    // Animation de la vidéo
    window.anime({
      targets: '.video-container',
      opacity: [0, 1],
      scale: [0.8, 1],
      translateY: [50, 0],
      duration: 1200,
      easing: 'easeOutExpo',
      delay: 400
    });

  }, []);

  // Animation au scroll pour les sections
  useEffect(() => {
    if (typeof window.anime !== 'function') return; // Attendre que anime.js soit chargé depuis le CDN

    const handleScroll = () => {
      const sections = document.querySelectorAll('.section-header');

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.8;

        if (isVisible && !section.classList.contains('animated')) {
          section.classList.add('animated');

          window.anime({
            targets: section.querySelector('.section-title'),
            opacity: [0, 1],
            translateY: [-30, 0],
            scale: [0.9, 1],
            duration: 800,
            easing: 'easeOutExpo'
          });

          window.anime({
            targets: section.querySelector('.section-subtitle'),
            opacity: [0, 1],
            translateY: [20, 0],
            duration: 800,
            easing: 'easeOutQuad',
            delay: 200
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Vérifier au chargement

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Gestion des contrôles vidéo personnalisés
  const togglePlay = () => {
    const video = videoRef.current;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVideoVolume(newVolume);
    if (newVolume > 0) {
      video.muted = false;
      setIsMuted(false);
    }
  };

  const handleProgressChange = (e) => {
    const video = videoRef.current;
    const newTime = (parseFloat(e.target.value) / 100) * video.duration;
    video.currentTime = newTime;
    setVideoProgress(parseFloat(e.target.value));
  };

  const toggleFullScreen = () => {
    const video = videoRef.current;
    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    }
  };

  // Mise à jour de la progression
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateProgress = () => {
      const progress = (video.currentTime / video.duration) * 100;
      setVideoProgress(progress);
    };

    const handleVideoClick = (e) => {
      // Un clic simple pour play/pause
      if (e.detail === 1) {
        setTimeout(() => {
          if (e.detail === 1) {
            togglePlay();
          }
        }, 200);
      }
    };

    const handleVideoDoubleClick = () => {
      // Double-clic pour plein écran
      toggleFullScreen();
    };

    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('click', handleVideoClick);
    video.addEventListener('dblclick', handleVideoDoubleClick);

    return () => {
      video.removeEventListener('timeupdate', updateProgress);
      video.removeEventListener('click', handleVideoClick);
      video.removeEventListener('dblclick', handleVideoDoubleClick);
    };
  }, []);

  // Gestion du son de la vidéo au survol
  useEffect(() => {
    const video = videoRef.current;
    const container = videoContainerRef.current;

    if (!video || !container) return;

    const handleMouseEnter = async () => {
      // Démarrer la vidéo si elle est en pause
      if (video.paused) {
        try {
          await video.play();
          setIsPlaying(true);
        } catch (error) {
          console.log("Autoplay bloqué par le navigateur:", error);
        }
      }
      // Activer le son automatiquement
      video.muted = false;
      setIsMuted(false);
    };

    container.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, []);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero hexagon-bg" id="home">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                L'Énergie Solaire
                <span className="gradient-text"> Pour Votre Avenir</span>
              </h1>
              <p className="hero-description">
                Sastra Energy vous accompagne depuis 2016 dans vos projets d'énergie renouvelable.
                Nos experts passionnés conçoivent des solutions photovoltaïques sur mesure pour
                votre maison, entreprise ou industrie.
              </p>
              <div className="hero-stats" ref={statsRef}>
                <div className={`stat ${hasAnimated ? 'stat-animated' : ''}`}>
                  <span className="stat-number">{yearsCount}+</span>
                  <span className="stat-label">Années d'expérience</span>
                </div>
                <div className={`stat ${hasAnimated ? 'stat-animated' : ''}`} style={{animationDelay: '0.1s'}}>
                  <span className="stat-number">{officesCount}</span>
                  <span className="stat-label">Bureaux en Tunisie</span>
                </div>
                <div className={`stat ${hasAnimated ? 'stat-animated' : ''}`} style={{animationDelay: '0.2s'}}>
                  <span className="stat-number">{solutionsCount}%</span>
                  <span className="stat-label">Solutions sur mesure</span>
                </div>
              </div>
              <div className="hero-buttons">
                <button className="btn-primary" onClick={() => onNavigate && onNavigate('references')}>
                  Nos Solutions
                </button>
                <button className="btn-secondary" onClick={() => onNavigate && onNavigate('contact')}>
                  Contactez-nous
                </button>
                {!isAuthenticated && (
                  <button className="btn-outline" onClick={() => onNavigate && onNavigate('login')}>
                    Se connecter / S'inscrire
                  </button>
                )}
              </div>
            </div>
            <div className="hero-image">
              {loadingImages ? (
                <div className="hero-loading">
                  <p>Chargement des images...</p>
                </div>
              ) : (
                <div className="hero-slideshow">
                  {installationImages.map((image, index) => (
                    <div
                      key={index}
                      className={`hero-slide ${index === currentImageIndex ? 'active' : ''}`}
                    >
                      <img src={image} alt={`Installation ${index + 1}`} />
                    </div>
                  ))}
                  {installationImages.length > 1 && (
                    <div className="hero-indicators">
                      {installationImages.map((_, index) => (
                        <span
                          key={index}
                          className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => setCurrentImageIndex(index)}
                        ></span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="video-section">
        <div className="container">
          <div className="video-container" ref={videoContainerRef}>
            <video
              ref={videoRef}
              muted
              loop
              className="project-video"
              style={{cursor: 'pointer'}}
            >
              <source src="/Projet-Tabarka.mp4" type="video/mp4" />
              Votre navigateur ne supporte pas la lecture de vidéos.
            </video>

            {/* Contrôles personnalisés style YouTube */}
            <div className="video-controls">
              <div className="progress-bar-container">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={videoProgress}
                  onChange={handleProgressChange}
                  className="progress-bar"
                />
              </div>

              <div className="controls-row">
                <div className="controls-left">
                  <button className="control-btn" onClick={togglePlay}>
                    {isPlaying ? '⏸' : '▶'}
                  </button>

                  <button className="control-btn" onClick={toggleMute}>
                    {isMuted || videoVolume === 0 ? '🔇' : videoVolume < 0.5 ? '🔉' : '🔊'}
                  </button>

                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={videoVolume}
                    onChange={handleVolumeChange}
                    className="volume-slider"
                  />
                </div>

                <div className="controls-right">
                  <button className="control-btn" onClick={toggleFullScreen}>
                    ⛶
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services" id="services">
        <div className="container">
          <div className="home-banner-header">

            <h2>Nos Solutions Énergétiques</h2>
            <p> Découvrez notre gamme complète de services photovoltaïques adaptés à tous vos besoins</p>

          </div>

          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">⚡</div>
              <h3>Solaire PV Réseau BT</h3>
              <p>Installations photovoltaïques raccordées au réseau basse tension pour particuliers et petites entreprises.</p>
            </div>

            <div className="service-card">
              <div className="service-icon">🏭</div>
              <h3>Solaire PV Réseau MT</h3>
              <p>Solutions industrielles raccordées au réseau moyenne tension pour grandes entreprises et industries.</p>
            </div>

            <div className="service-card">
              <div className="service-icon">💧</div>
              <h3>Pompage Solaire</h3>
              <p>Systèmes de pompage solaire pour l'irrigation agricole et l'approvisionnement en eau.</p>
            </div>

            <div className="service-card">
              <div className="service-icon">🏝️</div>
              <h3>Sites Isolés</h3>
              <p>Solutions autonomes pour sites non raccordés au réseau électrique traditionnel.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us hexagon-bg">
        <div className="why-choose-content">
          <div className="why-choose-text">
            <h2>Pourquoi Choisir Sastra Energy ?</h2>
            <div className="features-list">
              <div className="feature">
                <div className="feature-icon">✓</div>
                <div>
                  <h4>Expertise Reconnue</h4>
                  <p>Plus de 10 ans d'expérience dans le domaine photovoltaïque</p>
                </div>
              </div>

              <div className="feature">
                <div className="feature-icon">✓</div>
                <div>
                  <h4>Équipe Passionnée</h4>
                  <p>Des experts dédiés aux énergies renouvelables</p>
                </div>
              </div>

              <div className="feature">
                <div className="feature-icon">✓</div>
                <div>
                  <h4>Solutions Complètes</h4>
                  <p>De l'installation à la maintenance, nous vous accompagnons</p>
                </div>
              </div>

              <div className="feature">
                <div className="feature-icon">✓</div>
                <div>
                  <h4>Respect de l'Environnement</h4>
                  <p>Engagement fort pour les solutions écologiques</p>
                </div>
              </div>
            </div>
          </div>

          <div className="why-choose-image">
            <img
              src="/hero_backgrounds/hero_home_ultra.png"
              alt="SASTRA Energy - Installations solaires"
              className="why-choose-hero-img"
            />
          </div>
        </div>
      </section>

      {/* STEG Prompt Section - New Design with Background */}
      <section className="steg-prompt-section-new">
        <div className="steg-overlay"></div>
        <div className="container">
          <div className="steg-content-wrapper">
            {/* Logo SASTRA SOLAR */}
            <div className="steg-logo">
              <img src="/logosastra.png" alt="Sastra Solar Energy" className="steg-logo-image" />
            </div>

            {/* Titre Principal */}
            <h2 className="steg-title">ÉCONOMISEZ SUR VOTRE FACTURE STEG !</h2>

            {/* Sous-titres */}
            <div className="steg-subtitle">
              <p className="subtitle-line">
                💡 Vous avez un problème avec votre facture STEG ? Vous rêvez d'une facture à 0 DT ?
              </p>
              <p className="subtitle-main">
                Avec <strong>SASTRA Energy</strong>, ce rêve devient réalité grâce à nos solutions solaires innovantes !
              </p>
            </div>

            {/* Cartes de fonctionnalités */}
            <div className="steg-features">
              <div className="steg-feature-card">
                <div className="steg-feature-icon">
                  <div className="icon-circle">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="5" fill="#FFD700" />
                      <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41m11.32-11.32l1.41-1.41" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>
                <div className="steg-feature-text">
                  <h3>Énergie solaire<br />gratuite</h3>
                </div>
              </div>

              <div className="steg-feature-card">
                <div className="steg-feature-icon">
                  <div className="icon-circle">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#FFD700" />
                      <path d="M12 6v6l4 2" stroke="#1E3A5F" strokeWidth="2" strokeLinecap="round" />
                      <text x="12" y="16" fontSize="8" fill="#1E3A5F" textAnchor="middle" fontWeight="bold">€</text>
                    </svg>
                  </div>
                </div>
                <div className="steg-feature-text">
                  <h3>Économies<br />jusqu'à 100%</h3>
                </div>
              </div>

              <div className="steg-feature-card">
                <div className="steg-feature-icon">
                  <div className="icon-circle">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="#FFD700" />
                      <rect x="8" y="10" width="8" height="3" fill="#1E3A5F" />
                      <path d="M11 8l1-2 1 2 2 1-2 1-1 2-1-2-2-1z" fill="#FFD700" />
                    </svg>
                  </div>
                </div>
                <div className="steg-feature-text">
                  <h3>Installation<br />à domicile</h3>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="steg-buttons">
              <button
                className="steg-btn-devis"
                onClick={() => onNavigate && onNavigate('devis')}
              >
                OBTENIR UN DEVIS GRATUIT
              </button>
              <button
                className="steg-btn-realisations"
                onClick={() => onNavigate && onNavigate('references')}
              >
                Voir Nos Réalisations
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners-section" id="partners">
        <div className="container">
          <div className="home-banner-header">
            <h2>Nos Partenaires</h2>
            <p>Nous collaborons avec les meilleures marques internationales</p>
          </div>

          {isLoadingPartners ? (
            <div className="loading-partners">Chargement des partenaires...</div>
          ) : (
            <div className="partners-carousel">
              <div className="partners-track">
                {/* Premier groupe de partenaires */}
                {partners.map((partner) => (
                  <div key={partner._id} className="partner-slide">
                    <a href={partner.website} target="_blank" rel="noopener noreferrer">
                      <div className="partner-logo-wrapper">
                        {partner.logo && <img src={partner.logo} alt={partner.name} />}
                      </div>
                      <div className="partner-name">{partner.name}</div>
                    </a>
                  </div>
                ))}
                {/* Duplication pour animation continue */}
                {partners.map((partner) => (
                  <div key={`duplicate-${partner._id}`} className="partner-slide">
                    <a href={partner.website} target="_blank" rel="noopener noreferrer">
                      <div className="partner-logo-wrapper">
                        {partner.logo && <img src={partner.logo} alt={partner.name} />}
                      </div>
                      <div className="partner-name">{partner.name}</div>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>



    </div>
  );
};

export default Home;